import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import apiClient from '../api/apiClient';
import IntentsManager from '../components/flow-builder/IntentsManager';
import FlowsManager from '../components/flow-builder/FlowsManager';
import FlowEditor from '../components/flow-builder/FlowEditor';
import Layout from '../components/layout/Layout';

const FlowBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState('intents');
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Check permissions
  const canManageFlowBuilder = permissions.canManage('flowBuilder');
  const canViewFlowBuilder = permissions.canView('flowBuilder');
  const hasReadOnlyAccess = canViewFlowBuilder && !canManageFlowBuilder;

  console.log('FlowBuilder: Rendering with projectId:', projectId, 'activeTab:', activeTab);
  console.log('FlowBuilder: Auth state:', { currentUser: !!currentUser, isAuthenticated, authLoading });

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        console.log('FlowBuilder: Fetching project details for:', projectId);
        
        const response = await apiClient.get(`/projects/${projectId}`);
        console.log('FlowBuilder: Project details:', response);
        setProject(response.data.project);
      } catch (err) {
        console.error('FlowBuilder: Error fetching project:', err);
        setError('Failed to fetch project details');
      } finally {
        setLoading(false);
      }
    };

    if (projectId && isAuthenticated && !authLoading) {
      fetchProject();
    } else if (!authLoading && !isAuthenticated) {
      setError('User not authenticated');
      setLoading(false);
    } else {
      console.log('FlowBuilder: Waiting for auth or missing projectId:', { 
        projectId, 
        isAuthenticated, 
        authLoading 
      });
    }
  }, [projectId, isAuthenticated, authLoading]);

  const handleEditFlow = async (flow) => {
    try {
      console.log('FlowBuilder: Fetching complete flow data for editing:', flow._id);
      
      // Fetch complete flow data including all step details
      const response = await apiClient.get(`/flow-builder/projects/${projectId}/flows/${flow._id}`);
      const completeFlowData = response.data?.data?.flow || response.data?.flow;
      
      console.log('FlowBuilder: Complete flow data fetched:', completeFlowData);
      console.log('FlowBuilder: API Response structure:', response.data);
      console.log('FlowBuilder: Flow steps from API:', completeFlowData?.steps);
      
      // Debug step generateMessage fields
      if (completeFlowData?.steps) {
        completeFlowData.steps.forEach((step, index) => {
          console.log(`FlowBuilder: Step ${index} (${step.name}):`, {
            type: step.type,
            hasGenerateMessage: !!step.generateMessage,
            generateMessage: step.generateMessage,
            hasConfig: !!step.config,
            configGenerateMessage: step.config?.generateMessage
          });
          
          // CRITICAL DEBUG: Show generateMessage content for generate_message steps
          if (step.type === 'generate_message' && step.generateMessage) {
            console.log(`FlowBuilder: GENERATE MESSAGE DETAILS for ${step.name}:`, step.generateMessage);
          }
        });
      }
      
      setSelectedFlow(completeFlowData);
      setActiveTab('editor');
    } catch (error) {
      console.error('FlowBuilder: Error fetching complete flow data:', error);
      setError('Failed to load flow details');
      
      // Fallback to basic flow data if fetch fails
      setSelectedFlow(flow);
      setActiveTab('editor');
    }
  };

  const handleCreateFlow = () => {
    if (!canManageFlowBuilder) {
      console.log('FlowBuilder: User does not have permission to create flows');
      return;
    }
    setSelectedFlow(null); // null indicates new flow
    setActiveTab('editor');
  };

  const handleCloseEditor = () => {
    setSelectedFlow(null);
    setActiveTab('flows');
    setRefreshKey(prev => prev + 1); // Refresh flows list when explicitly closing
  };

  const handleSaveFlow = (savedFlow) => {
    // Keep the editor open but update with the saved flow data
    if (savedFlow) {
      setSelectedFlow(savedFlow);
    }
    // Refresh flows list in the background for when user eventually goes back
    setRefreshKey(prev => prev + 1);
  };

  const tabs = [
    { id: 'intents', name: 'Intents', icon: '🎯' },
    { id: 'flows', name: 'Flows', icon: '🌊' },
  ];

  if (loading) {
    console.log('FlowBuilder: Rendering loading state');
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    console.log('FlowBuilder: Rendering error state:', error);
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </Layout>
    );
  }

  console.log('FlowBuilder: Rendering main content, project:', project);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        

        {/* Debug Info */}
        {/* Removed debug info section for cleaner UI */}

        {/* Navigation Tabs */}
        {activeTab !== 'editor' && (
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    console.log('FlowBuilder: Switching to tab:', tab.id);
                    setActiveTab(tab.id);
                  }}
                  className={`
                    flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <span>{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'intents' && (
            <div>
              <div className="mb-4 p-4 bg-blue-50 rounded">
                <strong>Rendering IntentsManager</strong> for project: {projectId}
              </div>
              <IntentsManager 
                projectId={projectId} 
                project={project} 
                canManage={canManageFlowBuilder}
                hasReadOnlyAccess={hasReadOnlyAccess}
              />
            </div>
          )}
          
          {activeTab === 'flows' && (
            <div>
              <div className="mb-4 p-4 bg-green-50 rounded">
                <strong>Rendering FlowsManager</strong> for project: {projectId}
              </div>
              <FlowsManager 
                key={refreshKey}
                projectId={projectId} 
                onEditFlow={handleEditFlow}
                onCreateFlow={handleCreateFlow}
                canManage={canManageFlowBuilder}
                hasReadOnlyAccess={hasReadOnlyAccess}
              />
            </div>
          )}
          
          {activeTab === 'editor' && (
            <div>
              <div className="mb-4 p-4 bg-purple-50 rounded">
                <strong>Rendering FlowEditor</strong> for flow: {selectedFlow?.name || 'New Flow'}
              </div>
              <FlowEditor 
                projectId={projectId}
                flow={selectedFlow}
                onClose={handleCloseEditor}
                onSave={handleSaveFlow}
                canManage={canManageFlowBuilder}
                hasReadOnlyAccess={hasReadOnlyAccess}
              />
            </div>
          )}
          
          {!['intents', 'flows', 'editor'].includes(activeTab) && (
            <div className="p-4 bg-red-50 rounded">
              <strong>Unknown tab:</strong> {activeTab}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FlowBuilder; 